// frontend/src/components/planner/CreateRecurringModal.jsx
// Modal for creating a recurring forecast item from a register transaction

import React, { useState, useEffect, useMemo } from 'react';
import { addMonths, format, parseISO, getDate } from 'date-fns';
import { X, Calendar, DollarSign, Tag, Repeat, Building2 } from 'lucide-react';
import api from '../../utils/api';
import { useBusinessEntity } from '../../contexts/BusinessEntityContext';

const ITEM_TYPES = [
  { value: 'bill', label: 'Bill/Expense', color: 'orange' },
  { value: 'income', label: 'Income', color: 'emerald' },
  { value: 'debt', label: 'Debt Payment', color: 'red' }
];

// Static color mapping to avoid dynamic Tailwind class names (PurgeCSS strips them)
const colorStyles = {
  orange: {
    headerBg: 'bg-orange-600',
    submitBg: 'bg-orange-600',
    submitHover: 'hover:bg-orange-700',
    pillBg: 'bg-orange-50',
    pillBorder: 'border-orange-300',
    pillText: 'text-orange-700',
  },
  emerald: {
    headerBg: 'bg-emerald-600',
    submitBg: 'bg-emerald-600',
    submitHover: 'hover:bg-emerald-700',
    pillBg: 'bg-emerald-50',
    pillBorder: 'border-emerald-300',
    pillText: 'text-emerald-700',
  },
  red: {
    headerBg: 'bg-red-600',
    submitBg: 'bg-red-600',
    submitHover: 'hover:bg-red-700',
    pillBg: 'bg-red-50',
    pillBorder: 'border-red-300',
    pillText: 'text-red-700',
  },
};

const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 Weeks' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' }
];

/**
 * Determine item_type from a register transaction.
 * Plaid positive = outflow (bill), negative = inflow (income).
 * Register txn.signedAmount is already normalized: + = income, - = expense.
 */
function inferItemType(txn) {
  if (txn.item_type && txn.item_type !== 'unknown') return txn.item_type;
  return (txn.signedAmount > 0) ? 'income' : 'bill';
}

export default function CreateRecurringModal({ txn, accountsList, onClose, onSuccess }) {
  const { entities } = useBusinessEntity();

  // Derive defaults from the transaction
  const txnDate = txn.cleared_date || txn.scheduled_date || txn.date;
  const parsedDate = txnDate ? parseISO(txnDate + 'T00:00:00') : new Date();
  const defaultNextDate = format(addMonths(parsedDate, 1), 'yyyy-MM-dd');
  const defaultDayOfMonth = getDate(parsedDate);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);

  // Form state — pre-populated from transaction
  const [itemType, setItemType] = useState(inferItemType(txn));
  const [name, setName] = useState(txn.merchant_name || txn.name || '');
  const [amount, setAmount] = useState(String(Math.abs(parseFloat(txn.amount) || 0)));
  const [frequency, setFrequency] = useState('monthly');
  const [nextDueDate, setNextDueDate] = useState(defaultNextDate);
  const [dayOfMonth, setDayOfMonth] = useState(defaultDayOfMonth);
  const [bankAccountId, setBankAccountId] = useState(txn.bank_account_id ? String(txn.bank_account_id) : '');
  const [categoryId, setCategoryId] = useState('');
  const [categoryText, setCategoryText] = useState(txn.category || '');
  const [businessEntityId, setBusinessEntityId] = useState('');

  // Debt-specific fields
  const [interestRate, setInterestRate] = useState('');
  const [remainingBalance, setRemainingBalance] = useState('');
  const [minimumPayment, setMinimumPayment] = useState('');

  // Auto-detect entity from bank account
  const accountEntityMap = useMemo(() => {
    const map = {};
    entities.forEach(e => {
      (e.linkedAccountIds || []).forEach(accId => {
        map[accId] = e.id;
      });
    });
    return map;
  }, [entities]);

  useEffect(() => {
    if (txn.bank_account_id && accountEntityMap[txn.bank_account_id]) {
      setBusinessEntityId(String(accountEntityMap[txn.bank_account_id]));
    }
  }, [txn.bank_account_id, accountEntityMap]);

  // Load categories on mount
  useEffect(() => {
    async function loadCategories() {
      try {
        const response = await api.get('/api/household/categories');
        if (response.data.categories) {
          setCategories(response.data.categories);
          // If txn has a category name, try to find matching category_id
          if (txn.category) {
            const match = response.data.categories.find(
              c => c.name.toLowerCase() === txn.category.toLowerCase()
            );
            if (match) setCategoryId(String(match.id));
          }
        }
      } catch (err) {
        console.error('Error loading categories:', err);
      }
    }
    loadCategories();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep dayOfMonth in sync when nextDueDate changes
  useEffect(() => {
    if (nextDueDate) {
      const d = parseISO(nextDueDate + 'T00:00:00');
      setDayOfMonth(getDate(d));
    }
  }, [nextDueDate]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (!nextDueDate) {
      setError('Next due date is required');
      return;
    }

    // Debt validation
    if (itemType === 'debt') {
      if (!interestRate || parseFloat(interestRate) < 0) {
        setError('Debt requires a valid interest rate');
        return;
      }
      if (!remainingBalance || parseFloat(remainingBalance) < 0) {
        setError('Debt requires a valid remaining balance');
        return;
      }
    }

    try {
      setLoading(true);

      const payload = {
        item_type: itemType,
        name: name.trim(),
        amount: amountNum,
        frequency,
        next_due_date: nextDueDate,
        day_of_month: dayOfMonth,
        category_id: categoryId ? parseInt(categoryId, 10) : null,
        category: categoryText || null,
        bank_account_id: bankAccountId ? parseInt(bankAccountId, 10) : null,
        business_entity_id: businessEntityId ? parseInt(businessEntityId, 10) : null,
        // Pass source transaction so backend can create backfill instance + auto-match
        source_plaid_transaction_id: txn?.plaid_transaction_id || null,
        source_transaction_date: txn?.cleared_date || txn?.scheduled_date || txn?.date || null,
        ...(itemType === 'debt' && {
          interest_rate: parseFloat(interestRate),
          remaining_balance: parseFloat(remainingBalance),
          minimum_payment: minimumPayment ? parseFloat(minimumPayment) : null
        })
      };

      const response = await api.post('/api/planner/recurring-items', payload);

      if (response.data.success) {
        onSuccess(response.data.item);
      } else {
        setError(response.data.error || 'Failed to create recurring item');
      }
    } catch (err) {
      console.error('Error creating recurring item:', err);
      setError(err.response?.data?.error || 'Failed to create recurring item');
    } finally {
      setLoading(false);
    }
  }

  const selectedType = ITEM_TYPES.find(t => t.value === itemType);
  const styles = colorStyles[selectedType?.color] || colorStyles.emerald;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50">
        <div className="fixed inset-0" onClick={onClose} />

        <div className="relative bg-white w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-lg sm:mx-4 sm:rounded-xl rounded-t-2xl overflow-hidden flex flex-col shadow-xl">
          {/* Header */}
          <div className={`sticky top-0 ${styles.headerBg} px-4 sm:px-5 py-4 flex-shrink-0 z-10`}>
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-semibold text-white">Create Recurring Forecast</h3>
                <p className="text-base sm:text-sm text-white/80 truncate">
                  From: {txn.merchant_name || txn.name} — {format(parsedDate, 'MMM d, yyyy')}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center -mr-2"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-base sm:text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Item Type */}
            <div>
              <label className="block text-base sm:text-sm font-medium text-gray-700 mb-2">Type</label>
              <div className="flex gap-2">
                {ITEM_TYPES.map(type => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setItemType(type.value)}
                    className={`flex-1 px-3 py-2.5 sm:py-2 text-base sm:text-sm font-medium rounded-lg border transition-all min-h-[44px] ${
                      itemType === type.value
                        ? `${colorStyles[type.color].pillBg} ${colorStyles[type.color].pillBorder} ${colorStyles[type.color].pillText}`
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Netflix, Electric Bill"
                className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                autoFocus
              />
            </div>

            {/* Amount + Frequency — side by side */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">Amount</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    onWheel={(e) => e.target.blur()}
                    placeholder="0.00"
                    className="w-full pl-9 pr-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">Frequency</label>
                <div className="relative">
                  <Repeat className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <select
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 appearance-none bg-white"
                  >
                    {FREQUENCIES.map(f => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Next Due Date */}
            <div>
              <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">Next Due Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="date"
                  value={nextDueDate}
                  onChange={(e) => setNextDueDate(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <p className="text-base sm:text-xs text-gray-400 mt-1">
                Defaults to one month after the transaction ({format(parsedDate, 'MMM d')})
              </p>
            </div>

            {/* Bank Account */}
            <div>
              <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">Bank Account</label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={bankAccountId}
                  onChange={(e) => {
                    setBankAccountId(e.target.value);
                    // Auto-update entity when account changes
                    const accId = parseInt(e.target.value, 10);
                    if (accountEntityMap[accId]) {
                      setBusinessEntityId(String(accountEntityMap[accId]));
                    } else {
                      setBusinessEntityId('');
                    }
                  }}
                  className="w-full pl-9 pr-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 appearance-none bg-white"
                >
                  <option value="">No account</option>
                  {accountsList.map(acc => (
                    <option key={acc.id} value={acc.id}>
                      {acc.institutionName ? `${acc.institutionName} — ` : ''}{acc.nickname || acc.name} {acc.mask ? `(${acc.mask})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Category */}
            <div>
              <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
                Category <span className="text-gray-400">(optional)</span>
              </label>
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={categoryId}
                  onChange={(e) => {
                    setCategoryId(e.target.value);
                    const cat = categories.find(c => c.id === parseInt(e.target.value, 10));
                    if (cat) setCategoryText(cat.name);
                  }}
                  className="w-full pl-9 pr-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 appearance-none bg-white"
                >
                  <option value="">
                    {categoryText ? `Plaid: ${categoryText}` : 'No category'}
                  </option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Business Entity — only show if entities exist */}
            {entities.length > 0 && (
              <div>
                <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
                  Entity <span className="text-gray-400">(optional)</span>
                </label>
                <select
                  value={businessEntityId}
                  onChange={(e) => setBusinessEntityId(e.target.value)}
                  className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 appearance-none bg-white"
                >
                  <option value="">Personal</option>
                  {entities.map(e => (
                    <option key={e.id} value={e.id}>{e.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Debt-specific fields */}
            {itemType === 'debt' && (
              <div className="border-t border-gray-200 pt-4 space-y-3">
                <h4 className="text-base sm:text-sm font-medium text-gray-700">Debt Details</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-base sm:text-xs text-gray-500 mb-1">Interest Rate %</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={interestRate}
                      onChange={(e) => setInterestRate(e.target.value)}
                      placeholder="0.00"
                      className="w-full px-3 py-2.5 sm:py-2.5 sm:py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-base sm:text-xs text-gray-500 mb-1">Balance</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={remainingBalance}
                      onChange={(e) => setRemainingBalance(e.target.value)}
                      placeholder="0.00"
                      className="w-full px-3 py-2.5 sm:py-2.5 sm:py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-base sm:text-xs text-gray-500 mb-1">Min Payment</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={minimumPayment}
                      onChange={(e) => setMinimumPayment(e.target.value)}
                      placeholder="0.00"
                      className="w-full px-3 py-2.5 sm:py-2.5 sm:py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-4 py-3 sm:py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] text-base sm:text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`w-full sm:w-auto px-4 py-3 sm:py-2 ${styles.submitBg} text-white rounded-lg ${styles.submitHover} disabled:opacity-50 transition-colors min-h-[44px] text-base sm:text-sm font-medium`}
              >
                {loading ? 'Creating...' : 'Create Recurring Item'}
              </button>
            </div>
          </form>
        </div>
    </div>
  );
}
